import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import JoditEditor from "jodit-react";
import toastify from "../../config/toastify";
import { baseUrl } from "../../config/baseUrl";
import editorConfig from "../../utils/editorConfig";

const createEmptyPlan = (id = Date.now()) => ({
  id,
  validity: "",
  regularPrice: "",
  offerPrice: "",
  is_popular: false,
});

const normalizePlanForForm = (plans) => {
  if (!Array.isArray(plans) || !plans.length) {
    return [{ ...createEmptyPlan(1), is_popular: true }];
  }

  const hasPopularPlan = plans.some((plan) => Boolean(plan?.is_popular));

  return plans.map((plan, index) => ({
    id: plan?._id || `${plan?.validity || "plan"}-${index}`,
    validity: plan?.validity ? String(plan.validity) : "",
    regularPrice: plan?.regularPrice ? String(plan.regularPrice) : "",
    offerPrice: plan?.offerPrice ? String(plan.offerPrice) : "",
    is_popular: hasPopularPlan ? Boolean(plan?.is_popular) : index === 0,
  }));
};

export default function DailyMockCategoryFormPage({
  mode,
  pageTitle,
  breadcrumbTitle,
  successMessage,
}) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(mode === "edit");
  const [formval, setFormval] = useState({
    title: "",
    description: "",
    status: "active",
  });
  const [plans, setPlans] = useState([{ ...createEmptyPlan(1), is_popular: true }]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [existingImage, setExistingImage] = useState("");

  useEffect(() => {
    if (mode !== "edit" || !id) {
      return;
    }

    const fetchCategory = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${baseUrl}/daily/mock/category/${id}`, {
          headers: {
            Authorization: localStorage.getItem("token"),
          },
        });

        const categoryData = response?.data?.data;
        if (!categoryData) {
          toastify.error("Category not found");
          navigate("/daily-mock-categories");
          return;
        }

        setFormval({
          title: categoryData?.title || "",
          description: categoryData?.description || "",
          status: categoryData?.status || "active",
        });
        setPlans(normalizePlanForForm(categoryData?.plan));
        setExistingImage(categoryData?.image ? `${baseUrl}/${categoryData.image}` : "");
      } catch (error) {
        console.error("Error fetching daily mock category:", error);
        toastify.error("Failed to fetch category details");
        navigate("/daily-mock-categories");
      } finally {
        setLoading(false);
      }
    };

    fetchCategory();
  }, [id, mode, navigate]);

  const changeHandler = (event) => {
    const { name, value } = event.target;
    setFormval((previousValue) => ({
      ...previousValue,
      [name]: value,
    }));
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const removeSelectedFile = () => {
    setSelectedFile(null);
    setPreviewUrl("");
  };

  const addPlan = () => {
    setPlans((previousPlans) => [
      ...previousPlans,
      createEmptyPlan(Date.now()),
    ]);
  };

  const removePlan = (planId) => {
    if (plans.length === 1) {
      return toastify.error("At least one plan is required");
    }

    const remainingPlans = plans.filter((plan) => plan.id !== planId);
    if (!remainingPlans.some((plan) => plan.is_popular)) {
      remainingPlans[0].is_popular = true;
    }

    setPlans(remainingPlans);
  };

  const updatePlanField = (planId, field, value) => {
    setPlans((previousPlans) =>
      previousPlans.map((plan) =>
        plan.id === planId ? { ...plan, [field]: value } : plan,
      ),
    );
  };

  const handlePopularChange = (planId, checked) => {
    setPlans((previousPlans) =>
      previousPlans.map((plan) =>
        plan.id === planId
          ? { ...plan, is_popular: checked }
          : { ...plan, is_popular: false },
      ),
    );
  };

  const buildPlanPayload = () => {
    if (!plans.length) {
      throw new Error("At least one plan is required");
    }

    return plans.map((plan, index) => {
      const validity = Number(plan.validity);
      const regularPrice = Number(plan.regularPrice);
      const offerPrice = Number(plan.offerPrice);

      if (!validity || validity <= 0) {
        throw new Error(`Plan ${index + 1}: Validity is required`);
      }

      if (!regularPrice || regularPrice <= 0) {
        throw new Error(`Plan ${index + 1}: Regular price is required`);
      }

      if (!offerPrice || offerPrice <= 0) {
        throw new Error(`Plan ${index + 1}: Offer price is required`);
      }

      if (offerPrice > regularPrice) {
        throw new Error(`Plan ${index + 1}: Offer price cannot be greater than regular price`);
      }

      return {
        validity,
        regularPrice,
        offerPrice,
        is_popular: Boolean(plan.is_popular),
      };
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formval.title.trim()) {
      return toastify.error("Title is required");
    }

    if (!formval.description.trim()) {
      return toastify.error("Description is required");
    }

    let planPayload;

    try {
      planPayload = buildPlanPayload();
    } catch (error) {
      return toastify.error(error.message);
    }

    const formData = new FormData();
    formData.append("title", formval.title.trim());
    formData.append("description", formval.description.trim());
    formData.append("status", formval.status);
    formData.append("plan", JSON.stringify(planPayload));

    if (selectedFile) {
      formData.append("image", selectedFile);
    }

    try {
      const response =
        mode === "edit"
          ? await axios.put(`${baseUrl}/daily/mock/category/${id}`, formData, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            })
          : await axios.post(`${baseUrl}/daily/mock/category`, formData, {
              headers: {
                Authorization: localStorage.getItem("token"),
              },
            });

      if (!response?.data?.status) {
        toastify.error(response?.data?.message || "Some error occurred");
        return;
      }

      toastify.success(successMessage);
      navigate("/daily-mock-categories");
    } catch (error) {
      console.error("Error saving daily mock category:", error);
      toastify.error(error?.response?.data?.message || "Failed to save category");
    }
  };

  const config = useMemo(
    () => ({
      ...editorConfig,
      height: 400,
      placeholder: "Enter category description",
    }),
    [],
  );

  if (loading) {
    return (
      <section className="main-sec">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading category data...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="main-sec">
      <div className="row align-items-center">
        <div className="col-lg-6">
          <div className="dashboard-title">
            <h4 className="dash-head">
              <i className="fa fa-folder-open me-2" />
              {pageTitle}
            </h4>
          </div>
          <div className="custom-bredcump">
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/daily-mock-categories">Daily Mock Categories</Link>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  {breadcrumbTitle}
                </li>
              </ol>
            </nav>
          </div>
        </div>
        <div className="col-lg-6 d-flex justify-content-end">
          <Link to="/daily-mock-categories" className="btn btn-info text-white">
            <i className="fa fa-arrow-left me-1"></i>
            Back
          </Link>
        </div>
      </div>

      <div className="row">
        <div className="col-lg-12">
          <div className="cards edit-usr">
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-lg-6 mb-4">
                  <label htmlFor="title" className="form-label">
                    Title<sup className="text-danger">*</sup>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="title"
                    placeholder="Enter category title"
                    onChange={changeHandler}
                    value={formval?.title}
                  />
                </div>
                <div className="col-lg-6 mb-4">
                  <label htmlFor="status" className="form-label">
                    Status<sup className="text-danger">*</sup>
                  </label>
                  <select
                    className="form-control"
                    name="status"
                    onChange={changeHandler}
                    value={formval?.status}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
                <div className="col-lg-12 mb-4">
                  <label htmlFor="image" className="form-label">
                    Image
                  </label>
                  <input
                    type="file"
                    className="form-control"
                    id="image"
                    name="image"
                    accept="image/*"
                    onChange={handleFileChange}
                  />
                  {(previewUrl || existingImage) && (
                    <div className="mt-3">
                      <img
                        src={previewUrl || existingImage}
                        alt="daily mock category preview"
                        style={{ width: "180px", height: "120px", objectFit: "cover", borderRadius: "10px" }}
                      />
                      {selectedFile && (
                        <div className="mt-2">
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={removeSelectedFile}
                          >
                            Remove Selected File
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="col-lg-12 mb-4">
                  <label htmlFor="description" className="form-label">
                    Description<sup className="text-danger">*</sup>
                  </label>
                  <JoditEditor
                    value={formval?.description || ""}
                    config={config}
                    tabIndex={1}
                    onBlur={() => {}}
                    onChange={(value) => {
                      setFormval((previousValue) => ({
                        ...previousValue,
                        description: value,
                      }));
                    }}
                  />
                </div>

                <div className="col-lg-12 mb-4">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <label className="form-label mb-0">
                      Pricing Plans<sup className="text-danger">*</sup>
                    </label>
                    <button type="button" className="btn btn-sm btn-for-add text-white" onClick={addPlan}>
                      <i className="fa fa-plus me-1"></i>
                      Add Plan
                    </button>
                  </div>

                  {plans.map((plan, index) => (
                    <div className="plan-card mb-3" key={plan.id}>
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Plan {index + 1}</h6>
                        {plans.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => removePlan(plan.id)}
                          >
                            <i className="fa fa-trash"></i>
                          </button>
                        )}
                      </div>
                      <div className="row">
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Validity (Months)</label>
                          <input
                            type="number"
                            min="1"
                            className="form-control"
                            value={plan.validity}
                            onChange={(event) => updatePlanField(plan.id, "validity", event.target.value)}
                          />
                        </div>
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Regular Price</label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={plan.regularPrice}
                            onChange={(event) => updatePlanField(plan.id, "regularPrice", event.target.value)}
                          />
                        </div>
                        <div className="col-lg-3 mb-3">
                          <label className="form-label">Offer Price</label>
                          <input
                            type="number"
                            min="0"
                            className="form-control"
                            value={plan.offerPrice}
                            onChange={(event) => updatePlanField(plan.id, "offerPrice", event.target.value)}
                          />
                        </div>
                        <div className="col-lg-3 mb-3 d-flex align-items-end">
                          <div className="form-check mb-2">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={plan.is_popular || false}
                              onChange={(event) => handlePopularChange(plan.id, event.target.checked)}
                              id={`daily-mock-plan-popular-${plan.id}`}
                            />
                            <label className="form-check-label" htmlFor={`daily-mock-plan-popular-${plan.id}`}>
                              Popular Plan
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="col-lg-12 text-center">
                  <button type="submit" className="btn btn-for-add text-white">
                    <i className="fa fa-save me-1"></i>
                    Save Category
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .plan-card {
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          padding: 16px;
          background: #f8fafc;
        }
      `}</style>
    </section>
  );
}
